import { useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import {
  Flex,
  Input,
  PrimaryButton,
  SecondaryButton,
  type SegmentedControlOption,
  Spacing,
  Text,
} from '@enechain/ecloud-designsystem'

import { AccessibleSegmentedControl } from '~/components/common/AccessibleSegmentedControl'
import {
  EditIconButton,
  MasterDataTable,
} from '~/components/common/MasterDataTable'
import { FilterSection, FilterToolbar } from '~/components/common/filters'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import type { VoltageClass } from '~/models/voltageBreakdown'
import { AreaButtons } from '~/pages/CustomerInfo/components/AreaButtons'
import { CreateContractModal } from '~/pages/CustomerInfo/components/CreateContractModal'
import {
  CustomerFormModal,
  type SaveResult,
} from '~/pages/CustomerInfo/components/CustomerFormModal'
import { buildCustomerColumns } from '~/pages/CustomerInfo/components/customerColumns'
import {
  useListCustomers,
  useRenameCustomer,
} from '~/pages/CustomerInfo/hooks/useCustomerInfoQueries'
import {
  VOLTAGE_CLASSES,
  VOLTAGE_I18N_KEY,
  matchesCustomerFilter,
} from '~/pages/CustomerInfo/models/display'
import { resolveSupplyPointError } from '~/pages/CustomerInfo/models/resolveSupplyPointError'
import type { CustomerSummary } from '~/pages/CustomerInfo/models/types'

const ALL = 'all'
type VoltageFilter = typeof ALL | VoltageClass

/** 需要家一覧。 列: 需要家名 / 契約者 / エリア / 電圧階級。 */
export const CustomerListTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } = useListCustomers()
  const [areaCode, setAreaCode] = useState<AreaCode | undefined>(undefined)
  const [voltage, setVoltage] = useState<VoltageFilter>(ALL)
  const [search, setSearch] = useState('')
  const showSuccessToast = useSuccessToast()
  const rename = useRenameCustomer()
  // 編集モーダルには開いた時点の行 (楽観ロックの token の version を含む) を固定して渡す。
  // 背景再取得で差し替えると入力中の内容が消えるため、閉じるまで置き換えない (古い token の保存は CONFLICT になる)。
  const [editing, setEditing] = useState<CustomerSummary | undefined>()
  const [isCreateOpen, setIsCreateOpen] = useState(false)

  const handleSave = async (
    name: string,
    contractorId: string,
  ): Promise<SaveResult> => {
    if (editing === undefined) return undefined
    try {
      await rename.mutateAsync({
        customerId: editing.customerId,
        customerName: name,
        contractorId,
        expectedUpdatedAt: editing.version,
      })
      showSuccessToast({ title: t('page:customerInfo.list.toast.updated') })
      setEditing(undefined)
      return undefined
    } catch (err) {
      const resolved = resolveSupplyPointError(err, t)
      return resolved.reason === 'CONFLICT'
        ? { conflict: true }
        : { error: resolved.message }
    }
  }

  const voltageOptions: Array<SegmentedControlOption<VoltageFilter>> = [
    { value: ALL, label: t('page:customerInfo.common.all') },
    ...VOLTAGE_CLASSES.map((v) => ({
      value: v,
      label: t(VOLTAGE_I18N_KEY[v]),
    })),
  ]

  const rows = useMemo(
    () =>
      (data ?? []).filter((customer) =>
        matchesCustomerFilter(customer, {
          areaCode,
          voltageClass: voltage === ALL ? undefined : voltage,
          search,
        }),
      ),
    [data, areaCode, voltage, search],
  )

  const columns = buildCustomerColumns(t)

  return (
    <Flex flexDirection="column" gap={Spacing[3].value}>
      <FilterToolbar>
        <FilterSection label={t('page:customerInfo.common.area')}>
          <AreaButtons
            name="customer-info-list-area"
            onChange={setAreaCode}
            value={areaCode}
          />
        </FilterSection>
        <FilterSection label={t('page:customerInfo.list.filter.voltage')}>
          <AccessibleSegmentedControl
            aria-label={t('page:customerInfo.list.filter.voltage')}
            name="customer-info-voltage"
            onSelect={setVoltage}
            options={voltageOptions}
            selected={voltage}
          />
        </FilterSection>
        <FilterSection label={t('page:customerInfo.list.filter.search')}>
          <Input
            maxWidth="280px"
            aria-label={t('page:customerInfo.list.filter.search')}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('page:customerInfo.list.filter.searchPlaceholder')}
            type="search"
            value={search}
          />
        </FilterSection>
      </FilterToolbar>

      <Flex alignItems="center" justifyContent="space-between">
        <Text color="Text.midEmphasis" fontSize="md">
          {t('page:customerInfo.common.count', { count: rows.length })}
        </Text>
        <Flex alignItems="center" gap={2}>
          {isError && (
            <SecondaryButton onClick={() => void refetch()} size="sm">
              {t('page:customerInfo.common.retry')}
            </SecondaryButton>
          )}
          <PrimaryButton
            onClick={() => setIsCreateOpen(true)}
            size="sm"
            title={t('page:customerInfo.modal.createCustomerTitle')}
          >
            {t('page:customerInfo.action.add')}
          </PrimaryButton>
        </Flex>
      </Flex>

      <MasterDataTable
        columns={columns}
        emptyLabel={t('page:customerInfo.list.empty')}
        errorLabel={
          isError ? t('page:customerInfo.common.loadError') : undefined
        }
        getRowKey={(row) => row.customerId}
        isLoading={isLoading}
        onRowClick={(row) => setEditing(row)}
        renderRowActions={(row) => (
          <EditIconButton onClick={() => setEditing(row)} />
        )}
        rows={rows}
      />
      {isCreateOpen && (
        <CreateContractModal
          defaultAreaCode={areaCode ?? AreaCode.TOKYO}
          mode="customer"
          onClose={() => setIsCreateOpen(false)}
        />
      )}
      {editing !== undefined && (
        <CustomerFormModal
          customer={editing}
          isPending={rename.isPending}
          onClose={() => setEditing(undefined)}
          onSave={handleSave}
        />
      )}
    </Flex>
  )
}
